import { useState, useEffect, useRef, useMemo } from "react";

import { useSearchParams } from "react-router-dom";

import PropTypes from "prop-types";

import { useTranslation } from "react-i18next";

import { Grid, Button } from "@mui/material";

import BasicDialog from "../../base/BasicDialog";
import AutocompleteField from "../../field/AutocompleteField";

import LoaderWrapper from "../../wrapper/LoaderWrapper";

import { useAsync } from "../../../hooks/useAsync";

import TariffTable from "../../table/TariffTable";

import EquipmentDifferentialMonthParamsDetailsDialog from "../EquipmentDifferentialMonthParamsDetailsDialog";

import useDialogWithId from "../../../hooks/useDialogWithId";

import useEquipmentService from "../../../services/equipmentService";

import {
  isSearchParamsObjectEmpty,
  getFieldValueFromSearchParams,
  getObjectFromSearchParams,
  getFilterFieldKeyByNameAndPrefix,
} from "../../../helpers/methods";
import usePaginationWithSearchParams from "../../../hooks/usePaginationWithSearchParams";

import ControlPointOutlinedIcon from "@mui/icons-material/ControlPointOutlined";
import {
  centerVericalAlignIconStyle,
  overflowButtonStyle,
} from "../../../helpers/styles";

import {
  DIALOG_CREATE_MODE,
  DIALOG_EDIT_MODE,
  EDIT_COUNTER_DIFFERENTIAL_PERMISSION,
} from "../../../helpers/constants";

import {
  EQUIPMENT_DIFFERENTIAL_MONTH_PARAMS_TABLE_PREFIX,
  EQUIPMENT_DIFFERENTIAL_MONTH_PARAMS_TABLE_CONFIG,
} from "./TableConfig";

import useFilterSearchParams from "../../../hooks/useFilterSearchParams";

import useCheckPermission from "../../../hooks/usePermission";

const EquipmentDifferentialMonthParamsListDialog = (props) => {
  const { t } = useTranslation();

  const [hasPermissionToEditCounterDifferential] = useCheckPermission(
    EDIT_COUNTER_DIFFERENTIAL_PERMISSION
  );

  const tableConfig = useMemo(() => {
    let tableConfigTemp = EQUIPMENT_DIFFERENTIAL_MONTH_PARAMS_TABLE_CONFIG;

    if (!hasPermissionToEditCounterDifferential) {
      tableConfigTemp = tableConfigTemp.filter(
        (config) => config.name != "edit"
      );
    }

    return tableConfigTemp;
  }, [hasPermissionToEditCounterDifferential]);

  const {
    page,
    pageSize,
    handleChangePageWithSearchParams,
    handleChangeRowsPerPage,
    resetPageNumber,
    searchParams,
    setSearchParams,
  } = usePaginationWithSearchParams(
    EQUIPMENT_DIFFERENTIAL_MONTH_PARAMS_TABLE_PREFIX
  );

  const [dataLocaly, setDataLocaly] = useState();
  const [countRecords, setCountRecords] = useState();

  const {
    setNewItemSearchParamsIfAreChanged,
    getSearchParamsByFilterPrefix,
    clearSearchParamsByFilterPrefix,
  } = useFilterSearchParams(searchParams);

  const [filterSearchParams, setFilterSearchParams] = useState(
    getSearchParamsByFilterPrefix(
      EQUIPMENT_DIFFERENTIAL_MONTH_PARAMS_TABLE_PREFIX
    )
  );

  useEffect(() => {
    setNewItemSearchParamsIfAreChanged(
      EQUIPMENT_DIFFERENTIAL_MONTH_PARAMS_TABLE_PREFIX,
      filterSearchParams,
      setFilterSearchParams
    );
  }, [searchParams, EQUIPMENT_DIFFERENTIAL_MONTH_PARAMS_TABLE_PREFIX]);

  const { getEdmpListData, getEdmpFilteringData } = useEquipmentService();

  const filteringData = useAsync(getEdmpFilteringData);
  const backendData = useAsync(
    () => getEdmpListData(filterSearchParams),
    [filterSearchParams]
  );

  useEffect(() => {
    if (backendData.loading) {
      return;
    }
    setDataLocaly(backendData.value.results);
    setCountRecords(backendData.value.count);
  }, [backendData.loading]);

  const [existsChanges, setExistsChanges] = useState(false);
  const [openEdmpDialog, edmpId, onOpenEdmpDialog, onCloseEdmpDialog] =
    useDialogWithId();

  const edmpDialogModeRef = useRef();
  const handleOpenEdmpInCreateMode = () => {
    edmpDialogModeRef.current = DIALOG_CREATE_MODE;
    onOpenEdmpDialog(undefined);
  };

  const handleOpenEquipmentDifferentialMonthParamsDetailsDialogInEditMode = (
    ticketCategoryId
  ) => {
    edmpDialogModeRef.current = DIALOG_EDIT_MODE;
    onOpenEdmpDialog(ticketCategoryId);
  };

  const getEdmpSubmitCallback = () => {
    setDataLocaly(undefined);

    if (
      edmpDialogModeRef.current === DIALOG_CREATE_MODE &&
      !isSearchParamsObjectEmpty(filterSearchParams)
    ) {
      setSearchParams(
        clearSearchParamsByFilterPrefix(
          EQUIPMENT_DIFFERENTIAL_MONTH_PARAMS_TABLE_PREFIX
        )
      );
    } else {
      backendData.refetch();
    }

    setExistsChanges(true);
  };

  const isBuildingSelected = filterSearchParams?.building?.[0];
  const handleChangeBuilding = (
    e,
    value_object,
    value_key,
    state_value_name
  ) => {
    let newFormValue = Array.isArray(value_object)
      ? value_object.map((option) =>
          typeof option === "object" ? option[value_key] : option
        )
      : value_object[value_key];
    if (newFormValue === undefined) {
      newFormValue = null;
    }
    setSearchParams((prevSearchParams) => {
      let tempSearchParams = { ...getObjectFromSearchParams(prevSearchParams) };
      tempSearchParams[
        getFilterFieldKeyByNameAndPrefix(
          "building",
          EQUIPMENT_DIFFERENTIAL_MONTH_PARAMS_TABLE_PREFIX
        )
      ] = newFormValue;
      return tempSearchParams;
    });
  };

  const handleClose = () => {
    setSearchParams(
      clearSearchParamsByFilterPrefix(
        EQUIPMENT_DIFFERENTIAL_MONTH_PARAMS_TABLE_PREFIX
      )
    );
    props.onClose(existsChanges);
  };

  const isLoading = dataLocaly === undefined || filteringData.loading;

  const getDialogContent = () => {
    if (isLoading) return <LoaderWrapper showLoader={true} />;

    return (
      <Grid container spacing={1} justifyContent={"space-between"}>
        <Grid item xs={3}>
          <AutocompleteField
            name="building"
            multiple={false}
            isObjectOption={true}
            optionLabelKey={"object_name"}
            value={filterSearchParams?.building?.[0]}
            options={filteringData.value.building}
            label={t("table.epdm_table.building")}
            onChange={handleChangeBuilding}
            required
            disableClearable
          />
        </Grid>
        {isBuildingSelected && hasPermissionToEditCounterDifferential && (
          <Grid item xs={3}>
            <Button
              fullWidth
              variant="contained"
              size="small"
              startIcon={
                <ControlPointOutlinedIcon sx={centerVericalAlignIconStyle} />
              }
              onClick={handleOpenEdmpInCreateMode}
              sx={overflowButtonStyle}
            >
              {t("dialog.equipment.add_edmp")}
            </Button>
          </Grid>
        )}
        {isBuildingSelected && (
          <Grid item xs={12}>
            <TariffTable
              showFilters={true}
              data={dataLocaly}
              tableConfig={tableConfig}
              hiddenColumns={[]}
              filteringData={filteringData}
              filterPrefix={EQUIPMENT_DIFFERENTIAL_MONTH_PARAMS_TABLE_PREFIX}
              initialExpandedFilterHeader={
                !isSearchParamsObjectEmpty(filterSearchParams)
              }
              // style={{ maxHeight: "70vh" }}
              showCleanFilterIcon={true}
              showExportToFileButton={false}
              withPagination={true}
              countRecords={countRecords}
              page={page}
              onPageChange={handleChangePageWithSearchParams}
              rowsPerPage={pageSize}
              onRowsPerPageChange={handleChangeRowsPerPage}
              resetPageNumber={resetPageNumber}
              onClickEdit={
                handleOpenEquipmentDifferentialMonthParamsDetailsDialogInEditMode
              }
              showCheckbox={false}
                  isLoading={backendData.loading}
                  showTableLoader={true}
            />
          </Grid>
        )}
        {openEdmpDialog && (
          <EquipmentDifferentialMonthParamsDetailsDialog
            open={openEdmpDialog}
            onClose={onCloseEdmpDialog}
            dialogMode={edmpDialogModeRef.current}
            onSubmitCallback={getEdmpSubmitCallback}
            edmpId={edmpId}
          />
        )}
      </Grid>
    );
  };

  return (
    <BasicDialog
      open={props.open}
      onClose={handleClose}
      titleAlign="center"
      maxWidth={"lg"}
      title={t("dialog.equipment.edpm")}
    >
      {getDialogContent()}
    </BasicDialog>
  );
};

EquipmentDifferentialMonthParamsListDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
};

EquipmentDifferentialMonthParamsListDialog.defaultProps = {};

export default EquipmentDifferentialMonthParamsListDialog;
